import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import html2canvas from 'html2canvas';
import { useRef } from 'react';

import TooltipCustom from '@/components/TooltipCustom';
import ButtonSimple from '@/components/ButtonSimple';
import { IconAiOutlineDownload } from '@/utils/icons';

interface TableDataType {
  key: string;
  [key: string]: any;
  children?: TableDataType[];
}

interface HeaderType {
  title: string;
  data_key: string;
  sequence: number;
}

const PivotTableDynamic = ({ items }: any) => {


  const dataObj = items?.data?.[0];
  const pivotDataAll: any = dataObj?.data || [];
  const headers: HeaderType[] = dataObj?.headers || [];
  const tableRefPivotTableDynamic = useRef<HTMLDivElement>(null);
  const hideTotal: boolean = items?.hide_total || false;

  const sortedHeaders = [...headers].sort((a, b) => a.sequence - b.sequence);

  const transformDataForTable = (): TableDataType[] => {

    if (!pivotDataAll || !pivotDataAll.length) {
      return [];
    }

    const tableData: TableDataType[] = [];

    pivotDataAll.forEach((parentData: any, parentIndex: number) => {
      const parentRow: TableDataType = {
        key: `parent-${parentIndex}`,
        children: []
      };

      // Add only parent_key for parent rows, leave other columns empty
      sortedHeaders.forEach(header => {
        if (header.data_key === 'parent_key') {
          parentRow[header.data_key] = parentData.parent_key;
        } else {
          // Leave other columns empty for parent rows
          parentRow[header.data_key] = '';
        }
      });

      // Create child rows if child_data exists
      if (parentData.child_data && Array.isArray(parentData.child_data)) {
        parentData.child_data.forEach((childData: any, childIndex: number) => {
          const childRow: TableDataType = {
            key: `child-${parentIndex}-${childIndex}`
          };

          // Add all header data to child row
          sortedHeaders.forEach(header => {
            if (header.data_key === 'parent_key') {
              childRow[header.data_key] = childData.child_key || childData[header.data_key];
            } else if (header.data_key === 'parent_key_value') {
              childRow[header.data_key] = childData.child_key_value || childData[header.data_key];
            } else {
              childRow[header.data_key] = childData[header.data_key] || 0;
            }
          });

          parentRow.children!.push(childRow);
        });
      }

      tableData.push(parentRow);
    });

    return tableData;
  };

  const columns: ColumnsType<TableDataType> = sortedHeaders.map(header => ({
    title: header.title,
    dataIndex: header.data_key,
    key: header.data_key,
    className: 'text-[10px] !text-center !p-[4px] ',
    width: header.data_key === 'parent_key' ? 250 : 120,
    align: header.data_key === 'parent_key' ? 'left' : 'center',
    render: (value: any, record: TableDataType) => {
      if (record.children) {
        return <strong>{value == '0' || value == 0 ? '' : value}</strong>;
      }
      return value == '0' || value == 0 ? '' : value;
    }
  }));

  const tableData = transformDataForTable();

  // Calculate grand totals for each column
  const grandTotals: { [key: string]: number } = {};
  sortedHeaders.forEach(header => {
    if (header.data_key === 'parent_key') {
      grandTotals[header.data_key] = 0; // No total for text column
    } else {
      grandTotals[header.data_key] = pivotDataAll.reduce((sum: number, parent: any) => {
        if (header.data_key === 'parent_key_value') {
          return sum + (parent.parent_key_value || 0);
        } else {
          // Sum up all child values for year columns
          if (parent.child_data && Array.isArray(parent.child_data)) {
            return sum + parent.child_data.reduce((childSum: number, child: any) => {
              return childSum + (child[header.data_key] || 0);
            }, 0);
          } else {
            // If no child_data, add direct value
            return sum + (parent[header.data_key] || 0);
          }
        }
      }, 0);
    }
  });

  // If no data, show empty state
  // if (!data || !headers.length) {
  //   return (
  //     <div className="w-full p-8 text-center text-gray-500">
  //       No data available
  //     </div>
  //   );
  // }

  const exportAsPNG = async () => {
    if (tableRefPivotTableDynamic.current) {
      const canvas = await html2canvas(tableRefPivotTableDynamic.current, { scale: 2 });
      const link = document.createElement('a');
      link.download = `${items?.title || 'table'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };

  return (
    <div>

      <div>
        <TooltipCustom title="Export as PNG">
          <ButtonSimple
            onClick={exportAsPNG}
            className='flex justify-self-end mb-1'
          >
            <IconAiOutlineDownload />
          </ButtonSimple>
        </TooltipCustom>
      </div>

      <div className="w-full overflow-x-auto" ref={tableRefPivotTableDynamic}>
        <p className=" text-xl font-semibold leading-none tracking-tight base-card-title !text-center w-full mb-3 ">{items?.title}</p>
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          loading={false}
          style={{
            padding: 0,
            fontSize: 10,
            margin: 0
          }}
          bordered
          size="small"
          // scroll={{ y: '600px' }}
          defaultExpandAllRows={true}
          className="ant-table-striped p-0 !text-[8px]"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                {!hideTotal && (
                  <>
                    {sortedHeaders.map((header, index) => (
                      <Table.Summary.Cell
                        key={header.data_key}
                        index={index}
                        align={header.data_key === 'parent_key' ? 'left' : 'center'}
                      >
                        {header.data_key === 'parent_key' ? (
                          <strong>Total</strong>
                        ) : (
                          <strong>{grandTotals[header.data_key]}</strong>
                        )}
                      </Table.Summary.Cell>
                    ))}
                  </>
                )}
              </Table.Summary.Row>
            </Table.Summary>
          )}
          rowClassName={(record, index) => {
            if (record.children) {
              return 'bg-gray-50 font-semibold';
            }
            return index % 2 === 0 ? 'bg-white' : 'bg-gray-25';
          }}
        />
      </div>
    </div>
  );
};

export default PivotTableDynamic;